import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteMonitoringsFromAPI,
  fetchMonitoringsFromAPI,
  uploadMonitoringFileToAPI,
  downloadMonitoringFileFromAPI,
  registerBNFFromAPI
} from "../../../services/MonitoringService";

// Fetch Monitorings
export const fetchMonitorings = createAsyncThunk(
  "monitorings/fetchMonitorings",
  async ({ page, perPage, search }, { rejectWithValue }) => {
    try {
      const response = await fetchMonitoringsFromAPI(page, perPage, search);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch monitoring data.");
    }
  }
);

// Delete Monitoring
export const deleteMonitoring = createAsyncThunk(
  "monitorings/deleteMonitoring",
  async (monitoringId, { rejectWithValue }) => {
    try {
      const response = await deleteMonitoringsFromAPI(monitoringId);
      return monitoringId; // Return ID so we can remove it from the state
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete monitoring.");
    }
  }
);

export const registerBNF = createAsyncThunk(
  "monitorings/registerBNF",
  async (data, { rejectWithValue }) => {
    try {
      const response = await registerBNFFromAPI(data);
      return response; // Return ID so we can remove it from the state
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to register bnf.");
    }
  }
);


// Upload Monitoring File
export const uploadMonitoringFile = createAsyncThunk(
  "monitorings/uploadMonitoringFile",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await uploadMonitoringFileToAPI(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to upload monitoring file.");
    }
  }
);

// Download Monitoring File
export const downloadMonitoringFile = createAsyncThunk(
  "monitorings/downloadMonitoringFile",
  async ({ fileType, vendor_name }, { rejectWithValue }) => {
    try {
      const response = await downloadMonitoringFileFromAPI(fileType, vendor_name);
      return response;
    } catch (error) {
      // The service throws a plain Error carrying the server's message, so read
      // it off `.message`; `.response` only exists on raw axios errors.
      return rejectWithValue(error.message || "Failed to download monitoring file.");
    }
  }
);

const monitoringSlice = createSlice({
  name: "monitorings",
  initialState: {
    monitorings: [],
    currentPage: 1,
    lastPage: 1,
    // Monitoring-detail shows every part for the selected vendor at once (no
    // pagination in the table), so ask the backend for all rows in one page.
    perPage: 9999,
    firstItem: 1,
    monitoringSearch: "",
    loading: false,
    error: null,
    totalCount: 0,
    keywordSearch: "",
  },
  reducers: {
    setSearchMonitoring: (state, action) => {
      state.monitoringSearch = action.payload;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setVisibilityFilter: (state, action) => {
      state.currentFilter = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setKeywordSearch: (state, action) => {
      state.keywordSearch = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Monitorings
      .addCase(fetchMonitorings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMonitorings.fulfilled, (state, action) => {
        const monitorings = action.payload;
        state.loading = false;
        state.monitorings = monitorings.data;
        state.currentPage = monitorings.current_page;
        state.lastPage = monitorings.last_page;
        state.perPage = monitorings.per_page;
        state.firstItem = monitorings.from;
        state.totalCount = monitorings.total;
      })
      .addCase(fetchMonitorings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Monitoring
      .addCase(deleteMonitoring.fulfilled, (state, action) => {
        state.monitorings = state.monitorings.filter((monitoring) => monitoring.id !== action.payload);
      })
      .addCase(deleteMonitoring.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Upload Monitoring File
      .addCase(uploadMonitoringFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadMonitoringFile.rejected, (state, action) => {
        state.error = action.payload;
      })

            // register bnf Monitoring File
            .addCase(registerBNF.fulfilled, (state) => {
              state.loading = false;
            })
            .addCase(registerBNF.rejected, (state, action) => {
              state.error = action.payload;
            })
            
      // Download Monitoring File
      .addCase(downloadMonitoringFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadMonitoringFile.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  setSearchMonitoring,
  setPage,
  setVisibilityFilter,
  setPerPage,
  setKeywordSearch,
} = monitoringSlice.actions;
export default monitoringSlice.reducer;
